function isFormField(cmp) {
    return Boolean(cmp && typeof cmp === 'object' && 'value' in cmp && 'error' in cmp && 'required' in cmp && 'inputElm' in cmp);
}

function getErrorMessage(v) {
    if (typeof v === 'string') {
        return v;
    }
    if (typeof v === 'object') {
        return v.message;
    }
    return '';
}
function getInputValidity(cmp, inputElm) {
    // We need a copy of the validity object, because the input's validity object is read only
    const validity = getValidityObject(inputElm.validity);
    if (isFormField(cmp) && inputElm.validity.valueMissing && cmp.required === 'recommended') {
        // A warning is still considered valid
        validity.valid = true;
    }
    return {
        validationMessage: inputElm.validationMessage,
        validity,
    };
}
/**
 * Get validity from custom `validate` function or from inner HTML input element
 */
function _getValidity(cmp) {
    const value = cmp.value;
    // A hardcoded error always takes precedence
    if (cmp.error) {
        return {
            validationMessage: getErrorMessage(cmp.error),
            validity: getValidityObject({
                valid: false,
                customError: true,
            }),
        };
    }
    // A hardcoded warning also takes precedence
    if (cmp.warning) {
        return {
            validationMessage: getErrorMessage(cmp.warning),
            validity: getValidityObject({
                // A warning is not considered invalid
                valid: true,
            }),
        };
    }
    // Use external or internal custom validation function
    if (typeof cmp.validate === 'function' || typeof cmp.validateInternal === 'function') {
        const validationFunction = cmp.validate ? cmp.validate : cmp.validateInternal;
        const hasError = validationFunction?.(value);
        if (hasError) {
            const message = hasError instanceof Error ? hasError.message : hasError.validationMessage;
            const validity = hasError instanceof Error ? undefined : hasError.validity;
            const valid = hasError instanceof Error ? false : (hasError.validity.valid ?? true);
            return {
                validationMessage: message,
                validity: getValidityObject({
                    ...validity,
                    valid,
                    // It's only a custom error if it's from an external validation function
                    customError: !!(!valid && cmp.validate),
                }),
            };
        }
    }
    if (cmp.customValidation) {
        return {
            validationMessage: cmp.customValidation.msg,
            validity: getValidityObject({
                valid: cmp.customValidation.type === 'warning',
                customError: true,
            }),
        };
    }
    // Fallback to HTML input element
    if (cmp.inputElm) {
        cmp.inputElm.checkValidity();
        return getInputValidity(cmp, cmp.inputElm);
    }
    return {
        validationMessage: '',
        validity: getValidityObject(),
    };
}
function checkValidity(cmp) {
    return () => {
        const ret = _getValidity(cmp);
        // If going from invalid to valid
        const invalidToValid = cmp.validationMessage && !ret.validationMessage;
        // Update component with return of validity check
        cmp.validity = ret.validity;
        cmp.validationMessage = ret.validationMessage;
        cmp.validityChecked.emit();
        if (invalidToValid) {
            // Reset show validity flag. We don't want to nag the user again.
            cmp.showValidity = false;
            cmp.validityReported.emit();
        }
        return cmp.validity.valid;
    };
}
function hasReportValidity(elm) {
    return Boolean(elm &&
        typeof elm === 'object' &&
        'reportValidity' in elm &&
        typeof elm.reportValidity === 'function');
}
function reportValidity(cmp, getReportNodes) {
    return () => {
        cmp.checkValidity();
        if (cmp.validationMessage) {
            cmp.showValidity = true;
        }
        // Fire report validity on elements
        if (getReportNodes) {
            for (const elm of Array.from(getReportNodes() ?? [])) {
                if (hasReportValidity(elm)) {
                    elm.reportValidity();
                }
            }
        }
        cmp.validityReported.emit();
        return cmp.validity.valid;
    };
}
function setCustomValidity(cmp) {
    return (msg, type = 'error') => {
        if (msg) {
            cmp.customValidation = {
                msg: msg,
                type,
            };
        }
        else {
            cmp.customValidation = undefined;
        }
        // Will copy custom validation info to `cmp.validationMessage` and `cmp.validity`
        cmp.checkValidity();
    };
}
function setInputRef(cmp) {
    return (el) => {
        // At first render of the input element, copy input's
        // validation state into our own component
        if (!cmp.inputElm && el) {
            setTimeout(() => {
                const v = getInputValidity(cmp, el);
                cmp.validationMessage = v.validationMessage;
                cmp.validity = v.validity;
            }, 1);
        }
        cmp.inputElm = el;
    };
}
function getError(cmp) {
    return () => {
        // Error passed as prop takes precedence and will always be shown
        if (cmp.error) {
            return {
                type: 'error',
                message: getErrorMessage(cmp.error),
            };
        }
        if (cmp.warning) {
            return {
                type: 'warning',
                message: getErrorMessage(cmp.warning),
            };
        }
        if (!cmp.showValidity) {
            return {
                type: undefined,
                message: undefined,
            };
        }
        if (isFormField(cmp)) {
            // If the error is due to a required field, but we treat it as a recommended instead of required field
            if (cmp.validity.valueMissing && cmp.required === 'recommended') {
                return {
                    type: 'warning',
                    message: cmp.validationMessage,
                };
            }
        }
        let type;
        if (!cmp.validity.valid) {
            type = 'error';
        }
        else if (cmp.validationMessage) {
            // A valid result but with a validation message is considered as a warning
            type = 'warning';
        }
        return {
            type,
            message: cmp.validationMessage,
        };
    };
}
function validityStateToPlainObject(input) {
    return {
        badInput: input.badInput,
        customError: input.customError,
        patternMismatch: input.patternMismatch,
        rangeOverflow: input.rangeOverflow,
        rangeUnderflow: input.rangeOverflow,
        stepMismatch: input.stepMismatch,
        tooLong: input.tooLong,
        tooShort: input.tooShort,
        typeMismatch: input.typeMismatch,
        valid: input.valid,
        valueMissing: input.valueMissing,
    };
}
function getValidityObject(input) {
    const _input = input instanceof ValidityState ? validityStateToPlainObject(input) : input;
    return {
        badInput: false,
        customError: false,
        patternMismatch: false,
        rangeOverflow: false,
        rangeUnderflow: false,
        stepMismatch: false,
        tooLong: false,
        tooShort: false,
        typeMismatch: false,
        valid: true,
        valueMissing: false,
        ..._input,
    };
}
function watchError(cmp, newError, prevError) {
    if (newError !== prevError) {
        cmp.checkValidity();
    }
}
function watchWarning(cmp, newWarning, prevWarning) {
    if (newWarning !== prevWarning) {
        cmp.checkValidity();
    }
}
function getFormFieldAttributes(cmp) {
    const { type, message } = cmp.getError();
    return {
        disabled: cmp.disabled,
        requisite: cmp.required === true ? 'required' : undefined,
        ...cmp.formField,
        errorMessage: message,
        errorType: type,
    };
}
function getHostAttributes(cmp) {
    const errorType = cmp.getError().type;
    const ret = {
        'error-type': errorType,
    };
    if (!cmp.value) {
        ret['is-empty'] = true;
    }
    return ret;
}

export { setInputRef as a, getError as b, checkValidity as c, watchWarning as d, getFormFieldAttributes as e, getHostAttributes as f, getValidityObject as g, reportValidity as r, setCustomValidity as s, watchError as w };
